import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { useDefaultCamera } from "@/components/r3f/cameras/active-camera-context";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { getCameraParameters } from "@/components/r3f/utils/camera-parameters";
import { ModeTransitionProps } from "@/modes/mode";
import { useCached3DObject } from "@/object-cache";
import { selectShouldStoreCameraPosition } from "@/store/modes/export-mode-selectors";
import {
  prepareExport,
  setHiddenExportModeTabs,
  setShouldStoreCameraPosition,
} from "@/store/modes/export-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { IElementGenericPointCloudStream } from "@faro-lotv/ielement-types";
import { assert, WalkOrbitControls } from "@faro-lotv/lotv";
import { useMemo, type JSX } from "react";
import { Vector3 } from "three";
import { useComputeValidExportModeScene } from "./export-mode-state";
import { ExportModeTab, getTabsRequiringPointCloud } from "./export-mode-types";
import { useIsExportableDataset } from "./scans-export/use-prepare-scans-export-data";

/** @returns The transition to the export mode */
export function ExportModeTransition({
  onCompleted,
  previousMode,
}: ModeTransitionProps): JSX.Element | null {
  const dispatch = useAppDispatch();

  // If the point cloud stream is not available, there is still a chance that the scans export is supported,
  // Hence disabling the fallback by passing an empty function
  // so that the tabs related to point cloud/orthophoto export are hidden instead of switching mode
  const pointcloudStream = useComputeValidExportModeScene(() => {});
  const isScansExportSupported = useIsExportableDataset();

  const camera = useDefaultCamera();
  const target = useExportCameraTarget();

  const shouldStoreCameraPosition = useAppSelector(
    selectShouldStoreCameraPosition,
  );

  // The export state should be computed before the camera/target is changed by any user interaction
  useNonExhaustiveEffect(() => {
    // Determine which export mode tabs should be hidden based on the main element type
    const hiddenTabs: ExportModeTab[] = [];
    if (!pointcloudStream) {
      hiddenTabs.push(...getTabsRequiringPointCloud());
    }
    if (!isScansExportSupported) {
      hiddenTabs.push(ExportModeTab.scans);
    }

    assert(
      hiddenTabs.length <= Object.values(ExportModeTab).length,
      "Could not compute a valid set of export mode tabs to show",
    );

    dispatch(setHiddenExportModeTabs(hiddenTabs));

    if (previousMode === "start") {
      // When entering from a deep-link, we have no previous camera state to restore
      dispatch(
        prepareExport({
          returnMode: "overview",
          shouldRecenterCamera: true,
        }),
      );
    } else {
      dispatch(
        prepareExport({
          returnMode: previousMode,
          returnCamera: shouldStoreCameraPosition
            ? getCameraParameters(camera, target)
            : undefined,
          initialTarget: target?.toArray(),
        }),
      );

      // Reset the flag after reading its value
      dispatch(setShouldStoreCameraPosition(undefined));
    }

    onCompleted();
  }, [pointcloudStream]);

  // Not all the export modes require a point cloud stream to be present, e.g scans export
  if (pointcloudStream) {
    return <PointCloudStreamRenderer pointcloudStream={pointcloudStream} />;
  }

  return null;
}

/** @returns the point cloud stream for the export mode */
function PointCloudStreamRenderer({
  pointcloudStream,
}: {
  pointcloudStream: IElementGenericPointCloudStream;
}): JSX.Element {
  const mainObject = useCached3DObject(
    pointcloudStream,
    "ExportModePointCloudRenderer",
  );

  return <PointCloudRenderer pointCloud={mainObject} />;
}

/**
 * @returns the camera target for the export mode, computed from the state of the controls of the previous mode
 */
function useExportCameraTarget(): Vector3 | undefined {
  const viewContext = useViewRuntimeContext();
  return useMemo(() => {
    const controls = viewContext.controls?.[0];
    const target =
      controls instanceof WalkOrbitControls ? controls.target : undefined;
    return target;
  }, [viewContext.controls]);
}
